/* eslint-env node */
/* eslint-disable no-console */
import jwt from 'jsonwebtoken';
import config from 'sharedConfig';
import defaults from 'superagent-defaults';
import _ from 'lodash';
import { track } from './analytic';
import { sanitize } from './product';
import Promise from 'bluebird';

export let request = defaults(require('superagent-bluebird-promise'));
reset();

function reset() {
  request.clear();
  request.set('x-platform', 'web');
  request.query({ lat: 1 });
}

function productResult(result) {
  const products = _.isArray(result.body) ? result.body : [result.body];
  return products.map(item => {
    return sanitize(item, {
      sellerId: result.headers['x-seller-id'],
      noSeller: result.headers['x-no-seller']
    });
  });
}

export function categoryResult(list, headers) {
  return !list
    ? []
    : list.map(item => {
        return sanitize(item, {
          sellerId: headers['x-seller-id'],
          noSeller: headers['x-no-seller']
        });
      });
}

export let menuUrl = config.menu.baseUrl;

export function init(options) {
  menuUrl = options.menuUrl;
  reset();
}

export function setCsrfToken(token) {
  request.set('CSRF-Token', token);
}

export function getBaseMenu(opts) {
  var params = {};
  if (opts.categoryTree) params.categories = true;
  if (opts.curations) params.curations = true;
  if (opts.featuredImages) params.featured_images = true;
  return request
    .get(menuUrl + 'v3')
    .query(params)
    .then(res => {
      const body = res.body || {};
      if (body.categories)
        body.categories = categoryResult(body.categories, res.headers);
      if (body.curations) {
        Object.keys(body.curations).forEach(k => {
          const list = body.curations[k].products;
          body.curations[k].products = categoryResult(list, res.headers);
        });
      }
      return res.body;
    });
}

export function availableCategories() {
  return request
    .get(menuUrl + 'available-categories')
    .then(result => result.body)
    .catch(() => {});
}

let fetchedAt, cachedList;
const CATEGORIES_INTERVAL = 1000 * 60 * 10; // 10 minutes
export function fetchCategoryList() {
  if (fetchedAt && Date.now() - fetchedAt < CATEGORIES_INTERVAL) {
    return Promise.resolve(cachedList);
  }
  return request.get(menuUrl + 'categories').then(res => {
    const list = res.body;
    cachedList = list;
    fetchedAt = Date.now();
    return list;
  });
}

export function getProducts(skus, location) {
  let fetch = request.get(
    menuUrl + 'product/' + encodeURIComponent(skus.join(','))
  );
  if (location && location.lat && location.lng)
    fetch.query({ lat: location.lat, lng: location.lng });
  return fetch.then(productResult).then(result => {
    return _.sortBy(result, item => {
      return skus.indexOf(item.sku_id);
    });
  });
}

export function getProductsByUrl(url, location) {
  let fetch = request.get(menuUrl + 'product-name/' + url);
  if (location && location.lat && location.lng)
    fetch.query({ lat: location.lat, lng: location.lng });
  return fetch.then(productResult);
}

export function getProduct(sku, location) {
  return getProducts([sku], location).then(result => result[0]);
}

export function getProductPrices(sku) {
  return request
    .get(menuUrl + 'product/' + sku + '/prices')
    .then(res => res.body);
}

export function getRecommendation(skus) {
  return request
    .post(menuUrl + 'recommendation/checkout')
    .send({ skus: skus })
    .then(productResult);
}

export function getSimilarProducts(sku) {
  return request
    .get(menuUrl + `recommendation/similar/${sku}`)
    .then(result => [...categoryResult(result.body.similar, result.headers)]);
}

export function getRelatedProducts(sku) {
  return request.get(menuUrl + `recommendation/related/${sku}`).then(result => {
    const body = result.body || {};
    return {
      brands: categoryResult(body.brands, result.headers),
      similar: categoryResult(body.similar, result.headers),
      fbt: categoryResult(body.fbt, result.headers)
    };
  });
}

export function getSubstitutionProducts(sku) {
  return request
    .get(menuUrl + `recommendation/related/${sku}`)
    .query({ substitution: true })
    .then(result => {
      const body = result.body || {};
      return categoryResult(body.substitutes, result.headers);
    });
}

export function getUserRecommendations(user) {
  const userId = user.id || user.objectId;
  return request
    .get(menuUrl + `recommendation/user/${userId}`)
    .then(productResult);
}

export function getGenericRecommendations() {
  return request.get(menuUrl + `recommendation/generic`).then(productResult);
}

export function getFeaturedProducts() {
  return request.get(menuUrl + 'featured-product').then(productResult);
}

function withSearchOpts(body, opts = {}) {
  ['page', 'hitsPerPage', 'sort', 'filters', 'facets'].forEach(key => {
    if (opts[key]) body[key] = opts[key];
  });
  return body;
}

export function getCategory(urlName, opts = {}) {
  const body = withSearchOpts({ url_names: [urlName] }, opts);
  return request
    .post(menuUrl + 'search/v3/categories')
    .send(body)
    .then(result => {
      const body = result.body || {};
      const tags = Object.keys(body) || [];
      const tag = tags[0];
      const data = body[tag] || {};
      const { category, products, count, facets } = data;
      return {
        products: categoryResult(products, result.headers),
        category,
        count,
        facets
      };
    });
}

export function getCategoriesByTag(tags, opts = {}) {
  const body = withSearchOpts({ tags }, opts);
  return request
    .post(menuUrl + 'search/v3/categories')
    .send(body)
    .then(result => {
      const body = result.body || {};
      return tags
        .map(tag => {
          const apiData = body[tag];
          if (!apiData) return false;
          return {
            ...apiData,
            products: categoryResult(apiData.products, result.headers)
          };
        })
        .filter(Boolean);
    });
}

export function searchV3(query, opts = {}) {
  const body = withSearchOpts({ query }, opts);
  return request
    .post(menuUrl + 'search/v3')
    .send(body)
    .then(result => {
      return {
        products: categoryResult(result.body.products, result.headers),
        count: result.body.count,
        facets: result.body.facets
      };
    });
}

export function search(query) {
  return request
    .post(menuUrl + 'search/v2')
    .send({ query: query })
    .then(result => {
      return {
        filters: result.body.filters,
        products: categoryResult(result.body.products, result.headers)
      };
    });
}

export function searchFilters(query) {
  const requestPromise = request
    .post(menuUrl + 'search/v2/filter')
    .query({ size: 0 })
    .send({ query })
    .then(result => {
      return {
        filterset: result.body.aggs
      };
    });

  request.query({ size: null });

  return requestPromise;
}

export function searchProducts(query, filter) {
  const params = { query, ...filter };
  return request
    .post(menuUrl + 'search/v2/filter')
    .send(params)
    .then(result => {
      return {
        products: categoryResult(result.body.products, result.headers),
        count: result.body.count,
        filterset: result.body.aggs
      };
    });
}

export function categoryFilters(category) {
  const requestPromise = request
    .post(menuUrl + ['category-tags', category, 'filter'].join('/'))
    .query({ size: 0 })
    .then(result => {
      return {
        filterset: result.body.aggs
      };
    });

  request.query({ size: null });

  return requestPromise;
}

export function categoryProducts(category, filter) {
  return request
    .post(menuUrl + ['category-tags', category, 'filter'].join('/'))
    .send(filter)
    .then(result => {
      return {
        products: categoryResult(result.body.products, result.headers),
        count: result.body.count,
        filterset: result.body.aggs
      };
    })
    .then(result => {
      track('Product List Filtered', {
        list_id: category,
        filters: _.map(filter, (v, k) => ({ type: k, value: v })),
        sorts: [{ type: filter.sort, value: filter.order }],
        products: result.products.map(item => ({ product_id: item.sku_id }))
      });
      return result;
    });
}

export function categoryByUrl(slug) {
  return request
    .get(menuUrl + ['category-url', slug].join('/'))
    .then(result => result.body)
    .catch(result => result.body);
}

export function categories(tags, filter) {
  return request
    .get(menuUrl + ['category-tags', tags].join('/'))
    .query(filter)
    .then(function(result) {
      if (result.body.count != undefined)
        return {
          ...result.body,
          products: categoryResult(result.body.products, result.headers)
        };
      _.each(result.body, function(list, k) {
        result.body[k] = {
          ...list,
          products: categoryResult(list.products, result.headers)
        };
      });
      return result.body;
    });
}

export function deliveryFee() {
  return request.get(menuUrl + 'delivery-fee').then(res => res.body);
}

export function availability() {
  return request.get(menuUrl + 'availability').then(res => res.body);
}

export function availabilityForAddress({ lat, lng } = {}) {
  return request
    .query({ lat, lng })
    .get(menuUrl + 'availability')
    .then(res => res.body);
}

export function addCC(token) {
  return request
    .post('/order/card')
    .send({ token: token })
    .then(res => res.body);
}

export function selectCard(cardId) {
  return request
    .patch('/order/card')
    .send({ cardId })
    .then(res => res.body);
}

export function deleteCard(cardId) {
  return request
    .del('/order/card')
    .send({ cardId })
    .then(res => res.body);
}

export function fetchCards() {
  return request.get('/cards').then(res => res.body);
}

export function addPromo(code) {
  return request
    .post('/order/promo')
    .send({ code: code })
    .then(res => res.body);
}

export function fetchPromos(opts) {
  return request
    .get('/promos')
    .query(opts)
    .then(res => res.body);
}

export function checkout(params) {
  return request
    .post('/order/checkout')
    .send(params)
    .then(res => res.body);
}

export function orderStatus(id) {
  return request.get('/order/status/' + id).then(res => res.body);
}

export function getOrderHistory(params = {}) {
  return request
    .get('/order/history')
    .query(params)
    .then(res => res.body);
}

export function getOrderById(id) {
  return request
    .get('/order/detail')
    .query({ id })
    .then(res => res.body);
}

export function getProductReviews(sku_id, page) {
  return request
    .get(`/order/reviews/${sku_id}`)
    .query({ page })
    .then(res => res.body);
}

export function postProductReview(data) {
  return request
    .post('/order/reviews')
    .send(data)
    .then(res => res.body);
}

export function modifyOrder(order) {
  return request
    .patch(`/order/edit/${order._id}`)
    .send(order)
    .then(res => res.body);
}

export function tipRate({ id, amount, rating }) {
  let body = { id };
  if (amount) body.amount = amount;
  if (rating) body.rating = rating;
  return request
    .post('/order/tip/')
    .send(body)
    .then(res => res.body)
    .catch(err => {
      const message =
        err.body && err.body.message
          ? err.body.message
          : "We're sorry, an unexpected error occurred.";
      throw new Error(message);
    });
}

export function postOrderFeedback(id, { positive, negative, notes, thumb }) {
  let body = { positive, negative, thumb };
  if (notes) body.notes = notes;

  return request
    .post(`/order/feedback/${id}`)
    .send(body)
    .then(res => res.body)
    .catch(err => {
      const message =
        err.body && err.body.message
          ? err.body.message
          : "We're sorry, an unexpected error occurred.";
      throw new Error(message);
    });
}

export function getCopy() {
  return request
    .get('/copy')
    .then(res => res.body)
    .catch(err => {
      const message =
        err.body && err.body.message
          ? err.body.message
          : "We're sorry, an unexpected error occurred.";
      throw new Error(message);
    });
}

export function updateSubstitution({ user, order, status, products, save }) {
  return request
    .put(`/order/substitution/${order._id}`)
    .send({ user, status, products, save })
    .then(res => res.body);
}

export function vendor() {
  return request.get(menuUrl + '/vendor').then(result => result.body);
}

export function signupEvent() {
  return request.get('/event/signup').then(res => res.body);
}

export function emailCaptureEvent(email) {
  return request
    .post('/event/email-capture')
    .send({ email })
    .then(res => res.body);
}

export function getLoggedInUser() {
  return request.get('/me').then(res => res.body);
}

export function checkEmailForAccount(email) {
  return request
    .post('/customer/exists')
    .send({ email })
    .then(res => res.body);
}

export function sendPIN(timestamp) {
  return request
    .post('/customer/pin')
    .send({ timestamp })
    .then(res => res.body);
}

export function verifyPIN({ pin, timestamp }) {
  return request
    .post('/customer/pin/verify')
    .send({ pin, timestamp })
    .then(res => res.body);
}

export function addAddress(params) {
  return request
    .post('/order/address')
    .send(params)
    .then(res => res.body);
}

export function unsubscribeFromAppboy(email) {
  if (!email) return Promise.reject(new Error('Email required'));
  return request
    .post('/email/status')
    .send({ email, subscription_state: 'unsubscribed' })
    .then(res => res.body)
    .catch(err => console.log(err)); // eslint-disable-line no-console
}

export function subscribeToAppboy(email) {
  if (!email) return Promise.reject(new Error('Email required'));
  return request
    .post('/email/status')
    .send({ email, subscription_state: 'opted_in' })
    .then(res => res.body)
    .catch(err => console.log(err)); // eslint-disable-line no-console
}
export function thirdPartyAccountLink(data) {
  return request
    .post('/parse/users/account-link')
    .send({ username: data.email, password: data.password })
    .then(res => res.body)
    .catch(err => {
      return err.body;
    }); // eslint-disable-next-line no-console
}
export function forcePasswordResetToken(token, email) {
  let secret = process.env.JWT_SECRET || config.parse.secret;
  let payload = {
    id: token,
    group: 'web',
    user: email
  };
  let parseToken = jwt.sign(payload, secret, { expiresIn: '15m' });
  return request
    .set({ authorization: parseToken })
    .post('/parse/forceTokenReset')
    .send({ username: email })
    .then(res => res.body)
    .catch(err => {
      return err.body;
    }); // eslint-disable-next-line no-console
}

export function accountLinkPasswordReset(token, email, newPassword) {
  const appId = config.parse.appId;
  let body = { username: email, token: token, new_password: newPassword };

  return request
    .post(`/parse/apps/${appId}/request_password_reset?token=${token}`)
    .send(body)
    .then(res => {
      if (res.statusCode == 302) {
        return { message: 'success' };
      }
      if (res.statusCode == 403) {
        return { message: 'failed: link expired' };
      }
    })
    .catch(err => {
      console.error(err);
      return err.body;
    });
}
